/**
 * ForgottentPasswordPage Component
 */

import tr from '../../../../locales';
import * as actions from '../../../../redux/actions/user';
import { FieldEmail, FieldPassword } from '../../../../redux/form/utils';
import { validateEmail, validatePassword } from '../../../../utils/string';
import './styles.scss';

import { RouteMatch } from 'found';
import { size, template } from 'lodash';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { connect } from 'react-redux';
import { Form, FormProps, reduxForm, SubmissionError } from 'redux-form';
import { VelocityTransitionGroup } from 'velocity-react';

import Page from '../../../../containers/Page';
import Button from '../../../atoms/buttons/Button';
import FormError from '../../../atoms/feedback/FormError';
import TextField from '../../../atoms/forms/TextField';
import { IconLock, IconMail } from '../../../atoms/images/Icon';
import Heading from '../../../atoms/text/Heading';
import { Span } from '../../../atoms/text/Label';
import { LoginPagePath } from '../../../pages/account/Login';

export const ForgottenPasswordPagePath = '/forgotten-password';

interface IOwnDispatchProps {
  requestResetPassword: (
    email: string,
    resolve: (value?: any) => void,
    reject: (reason?: any) => void,
    code?: string,
    password?: string,
  ) => void;
}

type ForgottentPasswordPageProps =
  & FormProps<any, any, {}>
  & IOwnDispatchProps
  & RouteMatch;

interface IFormFields {
  email?: string;
  password?: string;
}

interface IOwnState {
  email?: string;
  error?: string;
  verificationCode?: string;
}

const classes = new BEM('forgotten-password');

class ForgottentPasswordPage extends React.Component<ForgottentPasswordPageProps, IOwnState> {
  constructor(props: ForgottentPasswordPageProps) {
    super(props);
    this.state = {
      email: props.location.query.email,
      error: props.location.query.error,
      verificationCode: props.location.query.code,
    };
  }

  onSubmit = (fields: IFormFields) => this.validate(fields).then(() =>
    new Promise((resolve, reject) => {
      const email = fields.email || this.state.email || '';
      const password = fields.password;
      const failureHandler = password ? this.onResetFailed : this.onRequestFailed;
      this.props.requestResetPassword(
        email,
        resolve,
        failureHandler(reject),
        this.state.verificationCode,
        password,
      );
    }))

  renderEmailSubmittedMessage() {
    return (
      <div key="forgottent-password-submitted" { ...classes('content') }>
        <Heading is="h2" { ...classes('title') }>All set!</Heading>
        <Heading is="h4" { ...classes('subtitle') }>
          We've just emailed you instructions on how to reset your password
        </Heading>
        <Span variant="note" size="medium">You may now close this window.</Span>
      </div>
    );
  }

  renderResetSuccessMessage() {
    return (
      <div key="forgottent-password-success" { ...classes('content') }>
        <Heading is="h2" { ...classes('title') }>Great!</Heading>
        <Heading is="h4" { ...classes('subtitle') }>Your password was reset. Now try to log in again.</Heading>
        <Button
          onClick={() => this.props.router.push(LoginPagePath)}
          label="Log In"
          colorScheme="dark"
          size="small-medium"
        />
      </div>
    );
  }

  renderForm() {
    const {
      handleSubmit,
      submitting,
    } = this.props;

    const {
      verificationCode,
    } = this.state;

    const error = this.props.error || this.state.error;

    let title: string;
    let subtitle: string;

    if (verificationCode) {
      title = '';
      subtitle = 'Enter a new password';
    } else {
      title = 'Forgot your password?';
      subtitle = `We'll email you instructions on how to reset it.`;
    }

    return (
      <div key="forgottent-password-form" { ...classes('content') }>
        <Heading is="h2" { ...classes('title') }>{title}</Heading>
        <Heading is="h4" { ...classes('subtitle') }>{subtitle}</Heading>
        <Form onSubmit={handleSubmit!(this.onSubmit)}>
          {error && <FormError error={error} />}
          {!verificationCode ? (
            <FieldEmail
              component={TextField}
              colorScheme="white"
              icon={<IconMail />}
              name="email"
              placeholder={tr.inputs.email}
              inlineButton={<Button isSubmit label="Send" colorScheme="dark" size="small-medium" />}
              disabled={submitting}
              onlyValidateIfTouched
              autoFocus
            />
          ) : (
              <FieldPassword
                component={TextField}
                colorScheme="white"
                icon={<IconLock />}
                name="password"
                placeholder={tr.inputs.password}
                disabled={submitting}
                inlineButton={<Button isSubmit label="Reset" colorScheme="dark" size="small-medium" />}
                warn
                autoFocus
              />
            )}
        </Form>
      </div>
    );
  }

  render() {
    return (
      <VelocityTransitionGroup
        component={Page}
        id="forgotten-password"
        enter={{ animation: 'transition.expandIn', duration: 500, delay: 350 }}
        leave={{ animation: 'transition.shrinkOut', duration: 350 }}
        runOnMount
      >
        {this.props.submitSucceeded ?
          !this.state.verificationCode ?
            this.renderEmailSubmittedMessage()
            : this.renderResetSuccessMessage()
          : this.renderForm()}
      </VelocityTransitionGroup>
    );
  }

  private validate({ email, password }: IFormFields) {
    return new Promise((resolve, reject) => {
      const errors: any = {};

      if (email) {
        if (email === '') {
          errors.email = tr.validations.required;
        } else if (!validateEmail(email)) {
          errors.email = tr.validations.email;
        }
      }

      if (password) {
        if (password === '') {
          errors.password = tr.validations.required;
        } else if (!validatePassword(password)) {
          errors.password = tr.validations.password;
        }
      }

      if (size(errors)) {
        reject(new SubmissionError(errors));
      } else {
        resolve();
      }
    });
  }

  private onRequestFailed = (reject: (reason?: any) => void) =>
    (err: Error) => {
      let message = '';
      let field: '_error' | 'email';

      switch (err.name) {
        case 'UserNotFoundException':
          message = 'Sorry, we don\'t know that email. Make sure it is well-written and maybe try again.';
          field = 'email';
          break;
        case 'LimitExceededException':
          message = 'Attempt limit exceeded, please try after some time.';
          field = '_error';
          break;
        case 'InvalidParameterException':
          /* tslint:disable-next-line */
          message = 'It seems you haven\'t yet confirmed your email address. You should have received an email from us, shortly after signing up, containing a link to confirm your email. Click that link and then you\'ll be able to reset your password.';
          field = '_error';
          break;
        default:
          message = tr.errors.genericMessage;
          field = '_error';
          break;
      }

      reject(new SubmissionError({ [field]: message }));
    }

  private onResetFailed = (reject: (reason?: any) => void) =>
    (err: Error) => {
      let message = '';
      let field: '_error' | 'password';

      switch (err.name) {
        case 'InvalidPasswordException':
          message = template(tr.errors.signup.invalidPassword)({ passwordPolicy: tr.validations.password });
          field = 'password';
          break;
        case 'LimitExceededException':
          message = 'Attempt limit exceeded, please try after some time.';
          field = '_error';
          break;
        case 'ExpiredCodeException':
          message = 'The provided code is expired. Enter your email and try again.';
          field = '_error';
          this.setState({
            email: undefined,
            verificationCode: undefined,
          });
          break;
        default:
          message = tr.errors.genericMessage;
          field = '_error';
          break;
      }

      reject(new SubmissionError({ [field]: message }));
    }
}

const ConnectedForgottentPasswordPage = connect(
  null,
  (dispatch): IOwnDispatchProps => ({
    requestResetPassword: (email, resolve, reject, code, password) => {
      dispatch(actions.requestResetPassword({
        code,
        email,
        promise: { resolve, reject },
        password,
      }));
    },
  }),
)(ForgottentPasswordPage);

const ConnectedForgottentPasswordPageReduxForm = reduxForm({
  form: 'forgotten-password',
})(ConnectedForgottentPasswordPage);

export default ConnectedForgottentPasswordPageReduxForm;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/pages/account/ForgottenPassword/index.tsx